import React from 'react';

import introImg from '../../../../assets/images/courses/teamplayer&collaboration/chapter9.1.jpg';
import chapterImg1 from '../../../../assets/images/courses/teamplayer&collaboration/chapter9.2.jpg';
import chapterImg2 from '../../../../assets/images/courses/teamplayer&collaboration/chapter9.3.jpg';
import chapterImg3 from '../../../../assets/images/courses/teamplayer&collaboration/chapter9.4.jpg';
import chapterImg4 from '../../../../assets/images/courses/teamplayer&collaboration/chapter9.5.jpg';
import chapterImg5 from '../../../../assets/images/courses/teamplayer&collaboration/chapter9.6.jpg';
import { CourseChapter } from '../../../../courses/courses.types';
import { MediaBlock } from '../../MediaBlock';

interface TeamPlayersAndCollaborationChapter1 {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}

export const TeamPlayersAndCollaborationChapter9: React.FC<TeamPlayersAndCollaborationChapter1> = ({
  chapter,
  onNextChapter,
  totalPages,
}) => {
  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };

  const handlePrint = () => {
    const printArea = document.getElementById('print-view');
    if (printArea) {
      printArea.classList.add('print-view');

      const cleanup = () => {
        printArea.classList.remove('print-view');
        window.onafterprint = null;
      };

      window.onafterprint = cleanup;

      window.print();
    }
  };

  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic  mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold text-3xl">Why is Collaboration Hard Sometimes</h2>
        <br />
        <MediaBlock mediaImage={introImg}></MediaBlock>
        <br />
        <p className="text-2xl text-center font-light mt-10 mb-10">
          Working with a team can sometimes be challenging. Even though most of your team members
          are easy to get along with, conflicts can still happen. This is normal and can occur for
          many reasons, such as different ideas, personality clashes, or communication problems.
        </p>
        <p className="text-xl text-center font-bold mt-10 mb-10">
          Understanding why these conflicts happen and learning how to handle them is key to
          becoming a better team player. This section will help you recognize common causes of team
          conflict, give you practical ways to handle high-friction situations, and teach you how to
          give constructive feedback. Additionally, it will explain the concepts of risky shift and
          groupthink, and how to avoid these pitfalls in team decision-making.
        </p>
        <br />
        <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">
          Why Is Collaboration Hard Sometimes?
        </h3>
        <p className="text-2xl text-center font-light mt-10 mb-10">
          Sometimes you must work with other people. Most people can be easy to work with, while
          other might be more challenging to work with. There can be conflict.
        </p>
        <h2 className="font-semibold text-xl">
          Collaboration can be challenging for several reasons:
        </h2>
        <div className="md:flex items-center mt-10 mb-10">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg1} alt="" />
          </div>
          <p className="flex-1 leading-7">
            One major factor is the <strong>diversity of ideas and perspectives</strong> within a
            team. While this diversity can lead to innovative solutions, it can also result in
            disagreements and conflicts.
          </p>
        </div>
        <div className="md:flex items-center mt-10 mb-10">
          <p className="flex-1  leading-7">
            <strong> Personal differences</strong>, such as varying communication styles and
            personality clashes, can further complicate teamwork. Additionally,{' '}
            <strong>miscommunication or lack of clear communication</strong> can create
            misunderstandings and hinder effective collaboration.
          </p>
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg2} alt="" />
          </div>
        </div>
        <p className="text-2xl text-center font-light mt-10 mb-10">
          These challenges are normal and expected in any team setting, but learning how to navigate
          them is essential for achieving common goals and maintaining a positive team dynamic.
          What’s important to remember is that:
        </p>
        <h2 className="font-semibold text-2xl text-center">Conflict is normal.</h2>
        <p className="text-lg text-left font-light mt-10 mb-10">
          Let’s explore the causes of team conflict in more detail. Click the button below.
        </p>
        <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">Causes of Team Conflict</h3>
        <p className="text-2xl text-center font-light mt-10 mb-10">
          The more diverse the team, the more variation in perspectives, and inevitably the more
          potential for conflict.
        </p>
        <div className="md:flex items-center mt-10 mb-10">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg3} alt="" />
          </div>
          <p className="flex-1 leading-7">
            Conflicts happen naturally when you have a team with different backgrounds, ideas, and
            perspectives. These differences can lead to misunderstandings and disagreements, but
            it's important to remember that conflicts aren't personal attacks. They're just a part
            of working with diverse people. When you see conflicts this way, you can deal with them
            better and create an environment where everyone's opinions are valued.
          </p>
        </div>
        <h2 className="font-semibold text-2xl text-left">Here's an example: </h2>
        <p className="text-lg text-left font-light mt-10 mb-10">
          When Jan travels on the subway with a group of people, it’s usually her that plans the
          route, but occasionally there's someone else that says, “No, no, no, you don't want to
          take the northern line, you want to take the central line and cut across from the east.”
          Jan’s reaction is to avoid any conflict and opt out and say, “Okay, whatever you think and
          just sits there on the train looking at her watch occasionally and thinking, ‘if we'd
          taken my route, we would've been there by now.’ As a team member, Jan is not contributing
          at all to the mission. In fact, she’s secretly hoping that it all goes wrong, so that she
          can prove that her way was better.
        </p>
        <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">
          Conflicts aren't personal; they're just part of having a diverse team.
        </h3>
        <div className="md:flex items-center mt-10 mb-10">
          <p className="flex-1 leading-7">
            <br />
            Conflicts happen naturally when you have a team with different backgrounds, ideas, and
            perspectives. These differences can lead to misunderstandings and disagreements, but
            it's important to remember that conflicts aren't personal attacks. They're just a part
            of working with diverse people. When you see conflicts this way, you can deal with them
            better and create an environment where everyone's opinions are valued.
          </p>
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg4} alt="" />
          </div>
        </div>
        <h2 className="font-semibold text-xl text-center">
          By understanding that conflicts are normal in a diverse team, you can approach them with a
          mindset focused on solving problems instead of taking things personally.
        </h2>
        <div className="md:flex items-center mt-10 mb-10">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg5} alt="" />
          </div>
          <p className="flex-1 leading-7">
            <br />
            Focusing on the bigger picture helps teams work together more effectively. When everyone
            keeps the main goal in mind, it's easier to set aside personal differences and
            collaborate towards a common objective. This helps turn conflicts from obstacles into
            opportunities for finding better solutions. By prioritizing shared goals, teams can use
            their diverse perspectives to boost creativity and come up with innovative ideas.
            Managing conflicts well not only improves how the team works together but also helps the
            team achieve more success and unity.
          </p>
        </div>
        <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">Reflect</h3> <br />
        <div id="print-view" className="mb-10 mt-10">
          <p className="text-lg font-semibold flex-1 mt-5 mb-5">
            Reflect and respond to the questions below. You may print your responses upon
            completion.
          </p>
          <ol className=" list-decimal pl-7 mb-14 text-xl font-light">
            <li>
              <p className="text-xl font-light flex-1 mt-5 mb-5">What's your pet conflict?</p>
              <textarea
                className="w-full  h-32 p-2 border rounded"
                placeholder="Enter your text here..."
              ></textarea>
            </li>
            <li>
              <p className="text-xl font-light flex-1 mt-5 mb-5">
                What annoys you when other people do it?
              </p>
              <textarea
                className="w-full  h-32 p-2 border rounded"
                placeholder="Enter your text here..."
              ></textarea>
            </li>
            <li>
              <p className="text-xl font-light flex-1 mt-5 mb-5">How do you usually respond?</p>
              <textarea
                className="w-full  h-32 p-2 border rounded"
                placeholder="Enter your text here..."
              ></textarea>
            </li>
            <li>
              <p className="text-xl font-light flex-1 mt-5 mb-5">
                Does it annoy you when people disagree with your suggested plan or when people slow
                the process down by asking everyone else if they're happy with the plan? Explain.
              </p>
              <textarea
                className="w-full  h-32 p-2 border rounded"
                placeholder="Enter your text here..."
              ></textarea>
            </li>
          </ol>

          <div className="mt-5 text-right">
            <button onClick={handlePrint} className="btn-internal">
              PRINT
            </button>
          </div>
        </div>
        <br />
        <p className="text-2xl text-center font-normal mt-5 mb-10">
          Next, let's look at how to confidently navigate conflict.
        </p>
        <br />
        <button onClick={handleNextChapterClick} className="btn-internal w-full">
          Confidently Navigate Conflict
        </button>
      </div>
    </>
  );
};
